import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { HandCoins, Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TerceroLookup } from "@/components/shared";

import { TREASURY_STATUS_BADGE_VARIANT } from "@/components/treasury/format";
import { useCashReceipts } from "@/lib/api/cash-receipts.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { CashReceiptStatus } from "@/lib/api/cash-receipts";
import type { TerceroSummary } from "@/lib/api/terceros";

export const Route = createFileRoute("/companies/$companyId/recibos/")({
  component: CashReceiptsIndexPage,
});

function CashReceiptsIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CashReceiptsIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function CashReceiptsIndexContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [customer, setCustomer] = useState<TerceroSummary | null>(null);
  const [status, setStatus] = useState<CashReceiptStatus | "all">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({
      ...(customer ? { terceroId: customer.id } : {}),
      ...(status !== "all" ? { status } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      page,
      pageSize: 50,
    }),
    [customer, status, dateFrom, dateTo, page],
  );

  const listQuery = useCashReceipts(companyId, filters);
  const receipts = listQuery.data?.receipts ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / 50));
  const hasFilters = !!customer || status !== "all" || !!dateFrom || !!dateTo;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("cashReceipts.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("cashReceipts.list.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link to="/companies/$companyId/recibos/new" params={{ companyId }}>
            <Plus className="h-4 w-4" />
            {t("cashReceipts.list.new")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 md:grid-cols-4">
          <div>
            <Label htmlFor="receipts-customer">
              {t("cashReceipts.list.filter_customer")}
            </Label>
            <TerceroLookup
              id="receipts-customer"
              companyId={companyId}
              kind="customer"
              value={customer}
              onChange={(next) => {
                setCustomer(next);
                setPage(1);
              }}
              allowCreate={false}
            />
          </div>
          <div>
            <Label htmlFor="receipts-status">
              {t("cashReceipts.list.filter_status")}
            </Label>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as CashReceiptStatus | "all");
                setPage(1);
              }}
            >
              <SelectTrigger id="receipts-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("treasury.statusFilter.all")}</SelectItem>
                <SelectItem value="posted">{t("treasury.status.posted")}</SelectItem>
                <SelectItem value="void">{t("treasury.status.void")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="receipts-from">
              {t("cashReceipts.list.filter_from")}
            </Label>
            <Input
              id="receipts-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <Label htmlFor="receipts-to">{t("cashReceipts.list.filter_to")}</Label>
            <Input
              id="receipts-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {listQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : listQuery.isError ? (
        <ErrorState
          title={t("cashReceipts.list.error_title")}
          description={t("cashReceipts.list.error_body")}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : receipts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <HandCoins className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">
                {hasFilters
                  ? t("cashReceipts.list.empty_search_title")
                  : t("cashReceipts.list.empty_title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasFilters
                  ? t("cashReceipts.list.empty_search_body")
                  : t("cashReceipts.list.empty_body")}
              </p>
            </div>
            {!hasFilters ? (
              <Button asChild>
                <Link
                  to="/companies/$companyId/recibos/new"
                  params={{ companyId }}
                >
                  <Plus className="h-4 w-4" />
                  {t("cashReceipts.list.new")}
                </Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("cashReceipts.table.number")}</TableHead>
                <TableHead>{t("cashReceipts.table.date")}</TableHead>
                <TableHead>{t("cashReceipts.table.method")}</TableHead>
                <TableHead className="text-right">
                  {t("cashReceipts.table.received")}
                </TableHead>
                <TableHead className="text-right">
                  {t("cashReceipts.table.applied")}
                </TableHead>
                <TableHead>{t("cashReceipts.table.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receipts.map((receipt) => (
                <TableRow key={receipt.id}>
                  <TableCell>
                    <Link
                      to="/companies/$companyId/recibos/$receiptId"
                      params={{ companyId, receiptId: receipt.id }}
                      className="font-mono text-sm font-medium text-primary hover:underline"
                    >
                      RC-{receipt.receiptNumber}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{receipt.receiptDate}</TableCell>
                  <TableCell className="text-sm">
                    {t(`treasury.method.${receipt.paymentMethod}`)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(receipt.amountReceived)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(receipt.totalApplied)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={TREASURY_STATUS_BADGE_VARIANT[receipt.status]}>
                      {t(`treasury.status.${receipt.status}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pageCount > 1 ? (
            <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {t("cashReceipts.list.page_of", { page, pageCount, total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t("common.back")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("common.next")}
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}
